import * as React from 'react';
import { LandingPageState } from './types';
import normalizeLandingPageData from './normalizeLandingPage';
import { PropsWithChildrenOnly } from '@ids/types/utility';
import {
    useConfig,
    useFetchedUniversalState,
    useUniversalFetch,
    useUniversalFetchingHelpers,
} from '@ids/appshell/hooks';
import { FetchedUniversalState, showOopsPage } from '@ids/appshell';

const initialLandinPageState = {
    data: null,
    errors: null,
    success: false,
    loading: false,
};

const LandingPageContext = React.createContext<LandingPageState>(initialLandinPageState);

LandingPageContext.displayName = 'LandingPageContext';

const LandingPageProvider = ({ children }: PropsWithChildrenOnly) => {
    const config = useConfig();
    const { get } = useUniversalFetchingHelpers();
    const data = useFetchedUniversalState('fetchLandingPage');

    useUniversalFetch(
        'fetchLandingPage',
        async () => {
            if (!data) {
                return get<FetchedUniversalState['fetchLandingPage']>({ path: '/web/v1/content/entries/ids' });
            }
            return undefined;
        },
        [data]
    );

    const value: LandingPageState = React.useMemo(
        () => ({
            data: normalizeLandingPageData(data, config),
            errors: data?.errors || null,
            success: !!data?.success,
            loading: !!(!data?.data?.items?.length && !data?.success && (!data?.errors?.length || !data?.errors)),
        }),
        [data, config]
    );

    if ((value?.errors || [])?.length > 0) {
        showOopsPage();
    }

    return <LandingPageContext.Provider value={value}>{children}</LandingPageContext.Provider>;
};

const useLandingPageContext = () => {
    const context = React.useContext(LandingPageContext);

    if (context instanceof Object && Object.keys(context).length === 0) {
        throw new Error('useLandingPageContext must be used within a LandingPageContextProvider');
    }

    return context;
};

const useIDSSku = () => {
    const { data } = useLandingPageContext();

    return data?.sku;
};

const useHeroBlock = () => {
    const { data } = useLandingPageContext();

    return data?.heroBlock;
};

const useOurProjectsBlock = () => {
    const { data } = useLandingPageContext();

    return data?.ourProjectsBlock;
};

const useProjectSliderBlock = () => {
    const { data } = useLandingPageContext();

    return data?.projectSliderBlock;
};

const useWhatYouGetBlock = () => {
    const { data } = useLandingPageContext();

    return data?.whatYouGetBlock;
};

const useHowItWorksBlock = () => {
    const { data } = useLandingPageContext();

    return data?.howItWorksBlock;
};

const useReviewsBlock = () => {
    const { data } = useLandingPageContext();

    return data?.reviewsBlock;
};

const useFAQBlock = () => {
    const { data } = useLandingPageContext();
    return data?.faqBlock;
};

const usePressBlock = () => {
    const { data } = useLandingPageContext();

    return data?.pressBlock;
};

const useQuestionnaireFormId = () => {
    const { data } = useLandingPageContext();
    return data?.questionnaireFormId;
};

export {
    LandingPageProvider,
    useLandingPageContext,
    useIDSSku,
    useHeroBlock,
    useOurProjectsBlock,
    useProjectSliderBlock,
    useHowItWorksBlock,
    useWhatYouGetBlock,
    useReviewsBlock,
    useFAQBlock,
    usePressBlock,
    useQuestionnaireFormId,
};
